/**
 * ============================================================================
 * usePeakHours - PEAK HOURS DATA HOOK
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. SPORT FILTER INTEGRATION
 *    - sportId in queryKey means changing sport triggers new fetch
 *    - Backend filters peak hours by sport
 *    - Don't filter in frontend, backend does it
 *
 * 2. DEPENDENT ON EARNINGS DATA
 *    - Could get peak hours from earnings response
 *    - But separate query allows sport filtering
 *    - Also allows independent caching
 *
 * 3. CACHE STRATEGY
 *    - Longer cache (10 min) - peak hours don't change often
 *    - No auto-refetch - user must explicitly change sport filter
 *    - Good for performance when user explores different sports
 *
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../services/api';
import type { PeakHour } from '../types/revenue';

/**
 * Fetches peak hours data, optionally filtered by sport
 *
 * @param venueId - The venue ID
 * @param sportId - Optional sport ID to filter by
 * @returns TanStack Query result with peak hours array
 *
 * @example
 * ```typescript
 * // All sports
 * const { data: peakHours } = usePeakHours(venue?.id);
 *
 * // Filtered by sport
 * const { data: peakHours } = usePeakHours(venue?.id, selectedSportId);
 *
 * // In component with sport selector
 * const [selectedSport, setSelectedSport] = useState<number | null>(null);
 * const { data: peakHours, isLoading } = usePeakHours(venue?.id, selectedSport);
 * // Changing selectedSport automatically refetches with new filter
 * ```
 */
export function usePeakHours(
  venueId: number | undefined,
  sportId: number | null | undefined = null
) {
  return useQuery<PeakHour[], Error>({
    // sportId in key means changing sport triggers refetch
    queryKey: ['peakHours', venueId, sportId],

    queryFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');

      const data = await vendorApi.getPeakHoursBySport(
        venueId,
        sportId ?? undefined
      );

      return data || [];
    },

    enabled: !!venueId,

    // Cache for 10 minutes
    staleTime: 10 * 60 * 1000,

    // No auto-refetch
    refetchInterval: false,

    // Refetch on window focus
    refetchOnWindowFocus: true,

    // Keep previous data while loading
    placeholderData: (previousData) => previousData,
  });
}
